import { AxiosInstance } from "axios";
import ApiClient from "./ApiClient";
import { 
  SubscriptionStatus, 
  StripeProduct, 
  CheckoutSessionRequest, 
  CheckoutSessionResponse, 
  PurchaseRecord 
} from "../types/Subscription";

// Mentoring Fusion API base URL
const API_URL = process.env.REACT_APP_MENTORING_API_URL ?? "";

class SubscriptionService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = new ApiClient(API_URL).getClient();
  }

  /**
   * Get available subscriptions for an organization.
   * Returns the list of available subscription products.
   */
  public getAvailableSubscriptions = async (
    organizationId: string
  ): Promise<StripeProduct[]> => {
    const response = await this.apiClient.get<any[]>(
      `/organizations/${organizationId}/subscriptions`
    );

    // Transform snake_case fields from API to camelCase expected by the UI
    const transformed: StripeProduct[] = response.data.map((product) => ({
      id: product.id,
      name: product.name,
      description: product.description,
      product_active: product.product_active,
      seat_credits: product.seat_credits ?? 0,
      prices: (product.prices || []).map((price: any) => ({
        id: price.id,
        amount: price.unit_amount, // convert to camelCase field the UI expects
        currency: price.currency,
        interval: price.recurring_interval ?? undefined,
        interval_count: price.recurring_interval_count ?? undefined,
      })),
    }));

    return transformed;
  };

  /**
   * Get current subscription status for an organization.
   * Returns the number of credits an organization has.
   */
  public getSubscriptionStatus = async (
    organizationId: string
  ): Promise<SubscriptionStatus> => {
    const response = await this.apiClient.get<SubscriptionStatus>(
      `/organizations/${organizationId}/subscriptions/status`
    );
    return response.data;
  };

  /**
   * Create a Stripe checkout session for purchasing a subscription
   */
  public createCheckoutSession = async (
    organizationId: string,
    request: CheckoutSessionRequest
  ): Promise<CheckoutSessionResponse> => {
    console.log(`Creating checkout session for organization ${organizationId}`);
    const response = await this.apiClient.post<CheckoutSessionResponse>(
      `/organizations/${organizationId}/subscriptions/checkout`,
      request
    );
    return response.data;
  };

  /**
   * Verify and record a completed purchase from Stripe webhook
   */
  public recordPurchase = async (
    organizationId: string,
    sessionId: string
  ): Promise<PurchaseRecord> => {
    console.log(`Recording purchase for session ${sessionId}`);
    const response = await this.apiClient.post<PurchaseRecord>(
      `/organizations/${organizationId}/subscriptions/purchases`,
      { session_id: sessionId }
    );
    return response.data;
  };

  /**
   * Get purchase history for an organization
   */
  public getPurchaseHistory = async (
    organizationId: string
  ): Promise<PurchaseRecord[]> => {
    const response = await this.apiClient.get<PurchaseRecord[]>(
      `/organizations/${organizationId}/subscriptions/purchases`
    );
    return response.data;
  };

  /**
   * Verify a purchase by session ID (for success page)
   */
  public verifyPurchase = async (
    organizationId: string,
    sessionId: string
  ): Promise<PurchaseRecord> => {
    const response = await this.apiClient.get<PurchaseRecord>(
      `/organizations/${organizationId}/subscriptions/purchases/${sessionId}`
    );
    return response.data;
  };
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
